import React, { useState, useEffect, useLayoutEffect, useRef } from "react";
import {
  View,
  TextInput,
  Animated,
  StyleSheet,
  Text,
  KeyboardTypeOptions,
  Platform,
  Image,
  TouchableOpacity,
  ActivityIndicator,
  type TextInputProps,
} from "react-native";
import Colors from "../../../constants/colors";
import { Flag } from "../../Flag";
import CloseIcon from "../../../../../../assets/images/CloseIcon.svg";
import WarningIcon from "../../../../../../assets/images/WarningIcon.svg";

type Props = {
  label?: string;
  value?: string;
  isAutoFocus?: boolean;
  testID?: string;
  onChangeText: (text: string) => void;
  editable?: boolean;
  keyboardType?: KeyboardTypeOptions;
  autoCapitalize?: 'none' | 'sentences' | 'words' | 'characters';
  /** Defaults false — spellcheck/autocorrect off for all product inputs unless overridden. */
  autoCorrect?: boolean;
  /** Defaults false — spell check underline (web/iOS). */
  spellCheck?: boolean;
  /** Defaults `"off"` — override e.g. `"tel"` only when needed. */
  autoComplete?: TextInputProps["autoComplete"];
  errorText?: string | null;
  /** When user types and there is an error, call this so parent can clear the error. */
  onClearError?: () => void;
  maxLength?: number;
  handleApplyPromoCode?: () => void;
  activityIndicatorStatus?: boolean;
  /**
   * First/middle/last name fields: disables iOS/Android autofill hints and reduces browser/password-manager
   * suggestions (does not collect payment data; helps avoid mistaken cc-* style heuristics on web).
   */
  blockPersonNameAutofill?: boolean;
  /** When set for DOB testIDs, floating label shows "Date" instead of "MM/DD/YYYY" after a complete date. */
  dobIsComplete?: boolean;
  /** Fires after internal focus handling (e.g. address suggestion dropdowns). */
  onFocus?: () => void;
  /** Fires after internal blur handling. */
  onBlur?: () => void;
};

/** For raw `TextInput` (same behavior as `FloatingTextInputComponent` defaults). */
const spellCheckOffTextInputProps = {
  autoCorrect: false,
  spellCheck: false,
  autoComplete: "off",
} as const;

/** Same as `FloatingTextInputComponent` defaults; for spreading on raw `TextInput`. */
export const nameFieldTextInputProps = spellCheckOffTextInputProps;
export const addressFieldTextInputProps = spellCheckOffTextInputProps;
export const inputAssistDisabledProps = spellCheckOffTextInputProps;

/**
 * Legal / display name fields (EnterSenderNameStep, ReceiverFullNameStep, BillPayEnterNameStep).
 * Uses the standard text keyboard (letters; not numeric/email). Word capitalization.
 * We use `default` (not `ascii-capable`) so Unicode letters (e.g. ñ, ü) work with Spanish keyboards.
 */
export const personNameFloatingKeyboardProps: Pick<
  Props,
  "keyboardType" | "autoCapitalize"
> = {
  keyboardType: "default",
  autoCapitalize: "words",
};

const FloatingTextInputComponent: React.FC<Props> = ({
  label,
  value,
  isAutoFocus,
  testID,
  onChangeText,
  editable = true,
  keyboardType,
  autoCapitalize,
  autoCorrect = false,
  spellCheck = false,
  autoComplete = "off" as TextInputProps["autoComplete"],
  errorText,
  onClearError,
  maxLength,
  handleApplyPromoCode,
  activityIndicatorStatus,
  blockPersonNameAutofill = false,
  dobIsComplete = false,
  onFocus: onFocusProp,
  onBlur: onBlurProp,
}) => {
  const [isFocused, setIsFocused] = useState(false);
  const inputRef = useRef<TextInput>(null);
  const dobIds = ["senderDOB", "receiverDOB", "billPayDOB"];
  const isDobField = Boolean(testID && dobIds.includes(testID));
  const hasDobValue = Boolean((value ?? "").trim().length > 0);

  const animated = useRef(
    new Animated.Value(isDobField ? (hasDobValue ? 1 : 0) : value ? 1 : 0)
  ).current;

  useEffect(() => {
    // DOB: float label only after user types (placeholder carries MM/DD/YYYY until then). Other fields unchanged.
    const toValue = isDobField ? (hasDobValue ? 1 : 0) : isFocused || !!value ? 1 : 0;
    Animated.timing(animated, {
      toValue,
      duration: 180,
      useNativeDriver: false,
    }).start();
  }, [isFocused, value, isDobField, hasDobValue]);

  /** `autoFocus` alone is unreliable inside scroll/keyboard views; `ref` was not wired so `focus()` never ran. */
  useLayoutEffect(() => {
    if (!isAutoFocus) return;
    inputRef.current?.focus();
  }, [isAutoFocus]);

  useEffect(() => {
    if (!isAutoFocus) return;
    const t = setTimeout(() => inputRef.current?.focus(), 120);
    const t2 = setTimeout(() => inputRef.current?.focus(), 400);
    return () => {
      clearTimeout(t);
      clearTimeout(t2);
    };
  }, [isAutoFocus]);

  useEffect(() => {
    // Caret only — do not set `color` on the DOM or placeholder inherits black (see web `WebkitTextFillColor` below).
    if (Platform.OS === "web" && inputRef.current) {
      const node = (inputRef.current as any)?._node || inputRef.current;
      if (node && node instanceof HTMLElement) {
        node.style.caretColor = "#111";
      }
    }
  }, []);

  const isCloseIconVisible = editable && (isFocused || (!!value && !!errorText));

  const labelStyle = {
    position: "absolute" as const,
    left: 12,
    top: animated.interpolate({
      inputRange: [0, 1],
      outputRange: [18, 6], // floated title top
    }),
    fontSize: animated.interpolate({
      inputRange: [0, 1],
      outputRange: [16, 14],
    }),
    lineHeight: 20,
    color: animated.interpolate({
      inputRange: [0, 1],
      outputRange: [Colors.infoSubValue, Colors.infoSubValue],
    }),
    backgroundColor: "transparent",
    fontFamily: "Rubik"
  };

  const borderColor = errorText
    ? Colors.errorRedColor
    : isFocused
    ? Colors.black
    : Colors.textInputBackground;

  const borderWidth = 2;

  const handleChangeText = (text: string) => {
    if (errorText) onClearError?.();
    onChangeText(text);
  };

  const handleClear = () => {
    // Prevent blur from killing focus
    onChangeText("");
    if (errorText) onClearError?.();
    // Keep focus active after clearing
    if (Platform.OS === "web") {
      setTimeout(() => inputRef.current?.focus(), 10);
    } else {
      inputRef.current?.focus();
    }
  };

  const dobPlaceholder = "MM/DD/YYYY";
  const dobFloatingTopLabel =
    hasDobValue && dobIsComplete ? "Date" : dobPlaceholder;
  const displayLabel = isDobField ? dobFloatingTopLabel : label;
  /** Empty DOB: no floating label — symmetric vertical padding so MM/DD/YYYY sits centered in the 56px field. */
  const dobPlaceholderOnly = isDobField && !hasDobValue;
  /** Shrunk label at top: add top padding so label and value have consistent spacing across all fields (incl. DOB). */
  const floatingLabelActive = (isFocused || !!value) && !dobPlaceholderOnly;

  const effectiveAutoComplete: TextInputProps["autoComplete"] = blockPersonNameAutofill
    ? ("off" as TextInputProps["autoComplete"])
    : autoComplete;

  const personNameNativeProps = blockPersonNameAutofill
    ? ({
        textContentType: "none" as const,
        importantForAutofill: "no" as const,
      } satisfies Pick<TextInputProps, "textContentType" | "importantForAutofill">)
    : null;

  const personNameWebProps =
    blockPersonNameAutofill && Platform.OS === "web"
      ? ({
          "data-lpignore": "true",
          "data-1p-ignore": "true",
          "data-bwignore": "true",
          "data-form-type": "other",
        } as Record<string, string>)
      : null;

  return (
    <View style={{ marginVertical: 8}}>
      <View style={{flexDirection: "row", alignItems: "center" }}>
        {testID === "phone-input" && (
        <View style={[styles.countryCodeSelector, { marginBottom: 1}]}>
          <View style={styles.flagContainer}>
            <Flag iso3="USA" width={24} height={24} />
          </View>
          <Text style={styles.plusText}>+1</Text>
        </View>
        )}
        <View style={[styles.container, { borderColor, borderWidth }]}>
          {(!isDobField || hasDobValue) && (
            <View pointerEvents="none" style={StyleSheet.absoluteFillObject}>
              <Animated.Text style={labelStyle}>{displayLabel}</Animated.Text>
            </View>
          )}
          <TextInput
            ref={inputRef}
            testID={testID}
            autoFocus={Boolean(isAutoFocus)}
            value={value}
            style={[
              styles.input,
              dobPlaceholderOnly && styles.inputDobPlaceholderCentered,
              floatingLabelActive && styles.inputFloatingLabelActive,
              { paddingRight: value ? 48 : 12 },
              dobPlaceholderOnly && { color: Colors.infoSubValue },
              Platform.OS === "web" && {
                WebkitTextFillColor: dobPlaceholderOnly
                  ? Colors.infoSubValue
                  : Colors.black,
              },
            ]}
            placeholder={isDobField && !hasDobValue ? dobPlaceholder : undefined}
            placeholderTextColor={Colors.infoSubValue}
            onFocus={() => {
              setIsFocused(true);
              onFocusProp?.();
            }}
            onBlur={() => {
              setIsFocused(false);
              onBlurProp?.();
            }}
            onChangeText={handleChangeText}
            editable={editable}
            keyboardType={keyboardType}
            autoCapitalize={autoCapitalize}
            autoCorrect={autoCorrect}
            spellCheck={spellCheck}
            autoComplete={effectiveAutoComplete}
            maxLength={maxLength}
            cursorColor={Colors.black}
            selectionColor={Colors.black} // highlight color
            {...(personNameNativeProps ?? {})}
            {...(personNameWebProps as object)}
          />
          
          {isCloseIconVisible ? (
            <TouchableOpacity testID="clearButton"
              style={styles.clearButton}
              onPress={handleClear}
              {...({ onMouseDown: (e: any) => e.preventDefault() } as any)}
              hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
            >
              <CloseIcon width={24} height={24}/>
            </TouchableOpacity>
          ) : null}
        </View>
      </View>
      <View style={{ flexDirection: 'row', alignItems: 'center'}}>
        {
          errorText && testID === "phone-input" && (
            <View style={{ marginRight: 8}}>
              <WarningIcon height={16} width={16}/>
            </View>
          )
        }
        {
          errorText ? <Text style={styles.errorText}>{errorText}</Text>
          : null}
      </View>
    </View>
  );
};

export default FloatingTextInputComponent;

const styles = StyleSheet.create({
  container: {
    flex: 1,
    height: 56,
    borderWidth: 2,
    borderRadius: 8,
    backgroundColor: Colors.textInputBackground,
    position: "relative",
    ...(Platform.OS === "web"
    ? ({
        borderStyle: "solid",
        outline: "none",
        transitionProperty: "border-color, border-width",
        transitionDuration: "150ms",
        transitionTimingFunction: "ease-in-out",
      } as any)
    : {}),
  },
  input: {
    height: "100%",
    paddingHorizontal: 12,
    paddingTop: Platform.OS === "web" ? 14 : 2,
    paddingBottom: 6,
    lineHeight: 24,
    fontSize: 16,
    fontWeight: "400",
    fontFamily: "Rubik",
    color: Colors.black,
    // ...( { outlineWidth: 0 } as any ),
    ...(Platform.OS === "web" && {
      outlineWidth: 0,
      outlineStyle: "none",
      border: "none",
      boxShadow: "none",
      backgroundColor: "transparent",
    } as any),
  },
  /** Non-DOB fields when label is floated: space below the small label before value/cursor (e.g. reference number). */
  inputFloatingLabelActive: {
    paddingTop: Platform.OS === "web" ? 24 : 16,
  },
  /** DOB empty state: label is hidden; offset `input` top padding so placeholder is vertically centered. */
  inputDobPlaceholderCentered: {
    paddingTop: 0,
    paddingBottom: 0,
    textAlignVertical: "center",
    ...(Platform.OS === "android" ? { includeFontPadding: false } : {}),
    ...(Platform.OS === "web"
      ? ({
          paddingTop: 16,
          paddingBottom: 16,
          lineHeight: 24,
        } as any)
      : {}),
  },
  errorText: {
    fontSize: 14,
    fontFamily: 'Rubik',
    fontWeight: '400',
    lineHeight: 20,
    color: Colors.errorRedColor,
    marginTop: 4,
  },
  clearButton: {
    position: "absolute",
    right: 4,
    top: 0,
    bottom: 0,
    width: 40,
    justifyContent: "center",
    alignItems: "center",
  },
  promoApplyButton: {
    position: "absolute",
    right: 10,
    top: "45%",
    transform: [{ translateY: -10 }],
    borderWidth: 1,
    borderRadius: 8,
    borderColor: Colors.applyText,
    backgroundColor: Colors.white,
    width: 70,
    height: 32,
    alignItems: 'center',
    justifyContent: 'center'
  },
  applyText: {
    fontFamily: 'Rubik',
    fontSize: 14,
    fontWeight: '400',
    color: Colors.applyText,
    lineHeight: 18,
  },
  countryCodeSelector: {
    height: 56,
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: Colors.textInputBackground,
    borderRadius: 8,
    paddingHorizontal: 16,
    paddingVertical: 6,
    marginRight: 8,
  },
  countryText: {
    fontSize: 14,
    fontWeight: "600",
    color: "#111827",
    marginRight: 4,
  },
  plusText: {
    fontSize: 16,
    fontFamily: 'Rubik',
    fontWeight: "400",
    lineHeight: 24,
    color: Colors.titleTextColor,
    marginLeft: 8
  },
  flagContainer: {
    height: 24,
    width: 24,
    borderRadius: 50,
    overflow: "hidden",
    alignItems: "center",
    justifyContent: "center",
  },
});